/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable no-console */
import qs from "qs";
import { mapInitialState as initialState, mapInitialState } from "@iventis/map-engine/src/defaults/map-initial-state";
import { BehaviorSubject, Subscription } from "rxjs";
import React, { useEffect, useReducer, useRef, useState } from "react";
import { queryParams, isValidUuid, EventStream, getAssetToCachedAsset, PROJECT_ID_QUERY_PARAM_KEY, API_KEY_QUERY_PARAM_KEY, canRead } from "@iventis/utilities";
import { ExportOptions } from "@iventis/map-engine/src/bridge/mapbox/engine-mapbox-types-and-constants";
import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import { v4 as uuid } from "uuid";
import { MapStoreEventType } from "@iventis/map-engine/src/types/events-store";
import { MapState, LayerStorageScope, MapModuleLayer, LayerDrawingControl, StoredPosition, Source } from "@iventis/map-engine/src/types/store-schema";
import { MAPBOX_MAX_ZOOM, MAPBOX_MIN_ZOOM } from "@iventis/map-engine/src/bridge/constants";
import { MapboxEngine } from "@iventis/map-engine/src/bridge/mapbox/engine-mapbox";
import { transformRequest } from "@iventis/map-engine/src/bridge/mapbox/tile-requests";
import { EventMessageMap } from "@iventis/map-engine/src/types/map-stream.types";
import { isLocalOnlyModelLayer } from "@iventis/map-engine/src/utilities/state-helpers";
import { InvocationEventType } from "@iventis/map-engine/src/types/events-invocation";
import { mapFontStackUrl } from "@iventis/map-engine/src/map-fonts";
import { IventisErrorBoundary } from "@iventis/error-boundaries";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { MicroService, RestrictedCdnSignature } from "@iventis/api-helpers";
import { Terrain } from "@iventis/domain-model/model/terrain";
import { setLocalMapObjectsDateFilterValue } from "@iventis/map-engine/src/utilities/date-time-filtering";
import { SitemapVersionType } from "@iventis/domain-model/model/sitemapVersionType";
import { Polygon } from "geojson";
import {
    assetUrlGetter,
    getMapTileUrl,
    multipleAssetUrlGetter,
    multipleAssetGetter,
    multipleModelsGetter,
    getSitemaps,
    getAsset,
    getMap,
    assetCacheService,
    getAttributeListItems,
    getTerrains,
    apis,
    getProjectDataFields,
} from "./api/routes";
import { AttributeThumbnailGenerator } from "./attribute-thumbnail-generator";
import { getSitemapVersionLevelsByConfig, getSitemapVersionLevelsByIds } from "./sitemap-helpers";

// Create a client
const queryClient = new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: false } } });

const mapboxKey = "pk.eyJ1Ijoiam9lY3VzZGluIiwiYSI6IjFmMzMzODhmYTQ4ZWM4MmYwZGVlZDMwMTJmMDIyZWQwIn0.8taIUk7YHSD1srUfcacXBA";

export const App: React.FunctionComponent = () => {
    const onError = (code: string, error: Error) => {
        window.dispatchEvent(new CustomEvent(InvocationEventType.MAP_ERRORED, { detail: error.message }));
    };
    return (
        <IventisErrorBoundary onError={onError}>
            <MapPage />
        </IventisErrorBoundary>
    );
};

const MapPage: React.FunctionComponent = () => {
    const storeRef = useRef<BehaviorSubject<MapState> | null>(null);
    const eventStreamRef = useRef<EventStream<EventMessageMap> | null>(null);
    const mapElementRef = useRef<HTMLDivElement>();
    const mapEngine = useRef<MapboxEngine>();
    const [layers, setLayers] = useState<MapLayer[] | undefined>(undefined);

    const modifyStore = (change: Partial<MapState>) => {
        storeRef.current!.next({
            ...storeRef.current!.value,
            ...change,
        });
    };
    useEffect(() => {
        console.log("Waiting for MAP_LOADED and THUMBNAILS_LOADED events to be dispatched...");
    }, []);

    // Manages the rendered state of the map and the thumbnails
    const [, send] = useReducer(
        (state: { mapLoaded: boolean; dataDrivenStyleThumbnailsLoaded: boolean }, action: { type: "MAP_LOADED" | "THUMBNAILS_LOADED" }) => {
            // Once both are loaded, send the window event
            if ((action.type === "MAP_LOADED" && state.dataDrivenStyleThumbnailsLoaded) || (action.type === "THUMBNAILS_LOADED" && state.mapLoaded)) {
                // eslint-disable-next-line no-console
                console.log("Both MAP_LOADED and THUMBNAILS_LOADED events received, dispatching MAP_LOADED event to window");
                window.dispatchEvent(new Event(InvocationEventType.MAP_LOADED));
            }
            if (action.type === "THUMBNAILS_LOADED") {
                // eslint-disable-next-line no-console
                console.log("THUMBNAILS_LOADED event received");
                return { ...state, dataDrivenStyleThumbnailsLoaded: true };
            }
            if (action.type === "MAP_LOADED") {
                // eslint-disable-next-line no-console
                console.log("MAP_LOADED event received");
                return { ...state, mapLoaded: true };
            }
            return { ...state };
        },
        { mapLoaded: false, dataDrivenStyleThumbnailsLoaded: false }
    );

    useEffect(() => {
        let storeSubscription: Subscription;

        const mapId = queryParams["map-id"];
        // Get the site map versions we want to use in the export
        const siteMapQueryParam = queryParams["sitemap-version-ids"];
        const siteMapVersionIds: string[] = siteMapQueryParam ? JSON.parse(siteMapQueryParam) : [];
        const dateFilter = queryParams["date-filter"];
        const timeFilterParams = queryParams["time-filter"];
        let timeFilter = null;
        if (timeFilterParams != null) {
            // Remove the colon
            timeFilter = timeFilterParams.replace(":", "");
        }

        const projectId = queryParams["project-id"];

        if (typeof mapId !== "string") {
            throw new Error("A map ID was not provided. Specify a map ID in the URL with &map-id=yourmapidhere");
        }

        let destroyed: boolean;

        getMap(mapId, projectId)
            .then(async ({ map, localObjects, mapObjectBounds }) => {
                if (destroyed) {
                    return;
                }

                const sitemaps = await getSitemaps();
                const signatures: RestrictedCdnSignature[] = [];
                const level = getLevel() ?? 0;

                let terrains: Terrain[] = [];
                if (map.terrainEnabled) {
                    terrains = await getTerrains();
                    const terrainAssets = await multipleAssetGetter(terrains.map((terrain) => terrain.id));
                    terrainAssets.forEach((asset) => {
                        signatures.push({
                            url: asset.authorityUrl,
                            signature: asset.signature,
                            expiry: asset.signatureExpiry,
                            assetVersion: asset.updatedAt,
                        });
                    });
                }

                const projectDataFields = await getProjectDataFields(projectId);

                let position: StoredPosition = mapInitialState.position.value;
                let sitemapVersionLevels: { id: string; perimeter: Polygon; type: SitemapVersionType }[] = [];
                if (sitemaps.length) {
                    // If siteMapQueryParam is provided, use the sitemap version ids from the query param
                    if (siteMapQueryParam != null) {
                        sitemapVersionLevels = getSitemapVersionLevelsByIds(siteMapVersionIds, sitemaps, level);
                    } else {
                        // Otherwise, use the sitemap configs from the map
                        sitemapVersionLevels = getSitemapVersionLevelsByConfig(map.sitemapConfigs, sitemaps, level);
                    }
                }
                const iventisLayers = map.layers.map((l) => parseLayerToIventisLayer(l));

                // Get date filtering values
                const dateFilterEnabled = timeFilter != null && dateFilter != null;
                const parsedTimeFilter = timeFilter != null ? parseInt(timeFilter, 10) : null;
                const parsedDateFilter = dateFilter != null ? parseInt(dateFilter, 10) : null;

                // If there is a date and time filter enabled need to ensure local map objects (models and line models) have their properties set
                if (dateFilterEnabled) {
                    setLocalMapObjectsDateFilterValue(localObjects, iventisLayers, parsedDateFilter, parsedTimeFilter, []);
                }

                let exportOptions = getMapExportOptions();

                // Only use the default saved map view or map bounds if no export options are provided
                if (exportOptions == null) {
                    if (map.defaultSavedMapViewId != null) {
                        const savedMapView = map.savedMapViews.find((smv) => smv.id === map.defaultSavedMapViewId);
                        if (savedMapView && savedMapView.position) {
                            const savedMapViewPosition = savedMapView.position;
                            position = {
                                lng: savedMapViewPosition.longitude,
                                lat: savedMapViewPosition.latitude,
                                bearing: savedMapViewPosition.bearing,
                                zoom: savedMapViewPosition.zoom,
                                pitch: savedMapViewPosition.pitch,
                                source: Source.HOST,
                            };
                        }
                    } else if (mapObjectBounds && mapObjectBounds.type === "Polygon" && Array.isArray(mapObjectBounds.coordinates[0])) {
                        const lats = mapObjectBounds.coordinates[0].map((c) => c[1]);
                        const lngs = mapObjectBounds.coordinates[0].map((c) => c[0]);
                        const minLat = Math.min(...lats);
                        const maxLat = Math.max(...lats);
                        const minLng = Math.min(...lngs);
                        const maxLng = Math.max(...lngs);
                        exportOptions = {
                            ...exportOptions,
                            bounds: [
                                [minLng, minLat],
                                [maxLng, maxLat],
                            ],
                            bearing: 0,
                            pitch: 0,
                        };
                    }
                }

                if (mapElementRef.current !== undefined) {
                    storeRef.current = new BehaviorSubject({
                        ...initialState,
                        layers: { value: iventisLayers, stamp: uuid() },
                        geoJSON: {
                            value: localObjects,
                            stamp: uuid(),
                        },
                        tileSources: {
                            value: {
                                objects: {
                                    tiles: [
                                        {
                                            name: "iventis",
                                            tiles: [getMapTileUrl(map.id)],
                                            type: "vector",
                                        },
                                    ],
                                },
                            },
                            stamp: uuid(),
                        },
                        position: {
                            value: position,
                            stamp: uuid(),
                        },
                        currentLevel: level,
                        datesFilter: {
                            value: {
                                filter: dateFilterEnabled,
                                time: parsedTimeFilter,
                                day: parsedDateFilter,
                            },
                            stamp: uuid(),
                        },
                        streetNames: {
                            value: map.streetNamesEnabled,
                            stamp: uuid(),
                        },
                        buildings3D: {
                            value: map.buildings3DEnabled,
                            stamp: uuid(),
                        },
                        terrain3D: {
                            value: {
                                enabled: sitemapVersionLevels.some((svl) => svl.type === SitemapVersionType.ThreeDimensional) ? false : map.terrainEnabled,
                                exaggeration: 1,
                                customTerrains: terrains,
                            },
                            stamp: uuid(),
                        },
                        selectedMapBackgroundId: map.backgroundId,
                        selectedSitemapVersionLevels: { value: sitemapVersionLevels.map(({ id, type }) => ({ id, type })), stamp: uuid() },
                        sitemapVersionLevelPerimeters: { value: sitemapVersionLevels.map(({ perimeter }) => perimeter), stamp: uuid() },
                        projectDataFields,
                    } as MapState);

                    eventStreamRef.current = new EventStream<EventMessageMap>();
                    const engine = new MapboxEngine({
                        store: { change: storeRef.current },
                        container: mapElementRef.current,
                        eventStream: eventStreamRef.current,
                        mbxAccessToken: mapboxKey,
                        preview: false,
                        modifierKeys: ["Control", "Meta"],
                        preserveDrawingBuffer: true,
                        assetOptions: {
                            // Route only accepts uuids, but some basemap ids may pass into here so we must filter them out
                            assetUrlGetter: async (id) => (isValidUuid(id) ? assetUrlGetter(id) : null),
                            multipleAssetUrlGetter: async (ids) => multipleAssetUrlGetter(ids),
                            bustCacheIds: () => null,
                            multipleModelsGetter,
                            isAssetSdf: async (id) => (await assetCacheService.get(id, async () => getAssetToCachedAsset(id, getAsset)))?.metaData?.sdf ?? false,
                        },
                        transformRequest: (url) =>
                            transformRequest(
                                url,
                                signatures,
                                queryParams[API_KEY_QUERY_PARAM_KEY],
                                storeRef.current.getValue().terrain3D.value.customTerrains,
                                queryParams[PROJECT_ID_QUERY_PARAM_KEY]
                            ),
                        maxZoom: MAPBOX_MAX_ZOOM,
                        minZoom: MAPBOX_MIN_ZOOM,
                        fontStackUrl: mapFontStackUrl,
                        exportOptions,
                        getAttributeListItems,
                        routeApiFunctions: undefined,
                        isExternalUser: false,
                        user: { id: "", isMobileUser: false },
                        setWebGLSupported: null,
                    });

                    mapEngine.current = engine;

                    storeSubscription = engine.storeEvents.subscribe((event) => {
                        switch (event.eventType) {
                            case MapStoreEventType.SET_MODE:
                                modifyStore({
                                    mode: {
                                        value: event.payload.mode,
                                        stamp: uuid(),
                                    },
                                });
                                break;
                            default:
                                // eslint-disable-next-line no-console
                                console.warn(`Map store event not handled '${event.eventType}'`);
                                break;
                        }
                    });

                    engine.invocationEvents.subscribe((e) => {
                        if (e.type === InvocationEventType.MAP_LOADED) {
                            send({ type: "MAP_LOADED" });
                        }
                    });
                }

                // Batch layers in groups of 30
                const batchedLayers = map.layers.reduce<MapLayer[][]>((acc, layer, index) => {
                    const batchIndex = Math.floor(index / 30);
                    if (!acc[batchIndex]) {
                        acc[batchIndex] = [];
                    }
                    acc[batchIndex].push(layer);
                    return acc;
                }, []);

                // Request authorisations for each batch
                const results = await Promise.all(
                    batchedLayers.map(async (batch) =>
                        apis[MicroService.LIBRARY]
                            // Backend returns an array of key-value pairs and it is not automatically converted to a record
                            .get<{ key: string; value: Authorisation }[]>(`hierarchy_permissions/resources-authorisations`, {
                                params: { resourceIds: batch.map((l) => l.id) },
                                paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
                            })
                            .then((response) => response.data.reduce<Record<string, Authorisation>>((acc, curr) => ({ ...acc, [curr.key]: curr.value }), {}))
                    )
                );

                // Filter map layers based on authorisation
                const layerAuthorisations = results.reduce((acc, curr) => ({ ...acc, ...curr }), {});
                setLayers(map.layers.filter((layer) => canRead(layerAuthorisations[layer.id])));
            })
            .catch((error) => {
                console.error(error);
                console.error("Error loading map, sending error event");
                // Send error to map renderer to finish export process early
                window.dispatchEvent(new CustomEvent(InvocationEventType.MAP_ERRORED, { detail: error.message }));
            });

        return () => {
            destroyed = true;
            mapEngine.current?.destroy();
            storeSubscription?.unsubscribe();
            storeRef.current?.unsubscribe();
            eventStreamRef.current?.destroy();
        };
    }, [mapElementRef.current]);

    return (
        <QueryClientProvider client={queryClient}>
            <div ref={mapElementRef as React.MutableRefObject<HTMLDivElement>} style={{ height: "100%", width: "100%" }} />
            {layers !== undefined && (
                <AttributeThumbnailGenerator
                    layers={layers}
                    onThumbnailsLoaded={() => {
                        send({ type: "THUMBNAILS_LOADED" });
                    }}
                />
            )}
        </QueryClientProvider>
    );
};

/**
 * Assumes any inputted layer is remote
 * @param layer
 * @returns
 */
export function parseLayerToIventisLayer(layer: MapLayer, remote = true): MapModuleLayer {
    return {
        ...layer,
        storageScope: isLocalOnlyModelLayer(layer) ? LayerStorageScope.LocalOnly : LayerStorageScope.LocalAndTiles,
        source: isLocalOnlyModelLayer(layer) ? layer.id : "iventis",
        stamp: "",
        selected: false,
        remote,
        drawingControls: {
            [LayerDrawingControl.COORDINATE_HANDLE]: false,
            [LayerDrawingControl.MID_POINT_HANDLE]: false,
            [LayerDrawingControl.ROTATION_HANDLE]: false,
        },
    };
}

export function getMapExportOptions(): ExportOptions | undefined {
    const urlParams = new URLSearchParams(window.location.search);
    const urlParamBounds = urlParams.get("map-bounds");
    const urlParamBearing = urlParams.get("bearing");
    const urlParamScale = urlParams.get("scale");
    const urlParamPitch = urlParams.get("pitch");
    if (urlParamBounds != null && urlParamBearing != null && urlParamPitch != null) {
        return {
            bounds: JSON.parse(urlParamBounds),
            bearing: Number(urlParamBearing),
            scale: urlParamScale != null ? Number(urlParamScale) : undefined,
            pitch: Number(urlParamPitch),
        };
    }
    return undefined;
}

export function getLevel(): number {
    const urlParams = new URLSearchParams(window.location.search);
    const level = Number(urlParams.get("level"));
    if (Number.isNaN(level) || level == null) {
        return null;
    }
    return level;
}
